// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the Cloudflare transport
//  ---------------------------------------------------------------------------
//  THE ONLY FILE IN THE APP THAT KNOWS CLOUDFLARE EXISTS. A room is a Durable
//  Object (E4) reached over a WebSocket; a blob is an R2 object (E3) reached
//  over fetch. Neither of those words appears above md-transport.ts, and the
//  day md-transport-nostr.ts is written it registers itself next to this one
//  and nothing else in the app is touched. That is the entire point of E2, and
//  it is worth more than anything this particular file does.
//
//  IT SENDS AND RECEIVES BINARY FRAMES. Not JSON, not text. A text frame would
//  mean encoding a sealed envelope into a string, and the moment a byte that is
//  not valid UTF-8 goes through a TextDecoder it comes back as U+FFFD and the
//  drawing is gone. `binaryType = 'arraybuffer'` is load-bearing.
//
//  A ROOM IS A SOCKET, AND SUBSCRIBERS SHARE IT. Two surfaces watching one
//  board is one connection, and the socket closes when the last of them leaves.
//  Publishes made before the socket is OPEN are held and sent on open rather
//  than dropped — a person draws the instant a board appears, well before a
//  WebSocket handshake finishes, and a stroke lost to that race is a stroke
//  they have to draw again.
//
//  A DROPPED SOCKET RECONNECTS, with backoff and a ceiling. Phones change
//  networks constantly. What it does NOT do is replay: catching up belongs to
//  the CRDT above (E5), which can merge, and not to a transport, which cannot.
//
//  THE ROOM IS ASKED BEFORE THE SOCKET IS OPENED, and that is not politeness.
//  A WebSocket that cannot be established reports nothing a caller can read: it
//  just closes, and the loop above tries again at the fifteen-second ceiling for
//  as long as the tab is open. On an origin whose Worker predates the route that
//  is a doomed request every fifteen seconds, forever, saying nothing — and on a
//  preview, where there is deliberately no ROOM binding, it is the same. A plain
//  GET to the same path answers 426 when the room is real (see MdRoom.fetch) and
//  says what is wrong when it is not, so `subscribe` can hand back a sentence
//  instead of an optimistic yes. It costs one round trip before the handshake;
//  publishes made in the meantime are held, as they already were.
//
//  That answer is what keeps the URL in this file. Above the seam, md-live.ts
//  learns whether it is in a room by asking to be in one — it has no path, no
//  origin and no status code, which is the whole point of E2.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  const RETRY_FLOOR = 500;            // ms before the first reconnect
  const RETRY_CEILING = 15000;        // and never further apart than this
  const CONGESTED_AT = 512 * 1024;     // WebSocket bufferedAmount
  const RELIABLE_QUEUE_MAX = 8 * 1024 * 1024;
  const EPHEMERAL_KEYS_MAX = 128;

  /* What a room says to a request that is not an upgrade. One status, and the
     only one that means "this is a real room and it is yours": 404 is an origin
     without the route, 503 an origin without the binding, 401 a session that
     has gone, 403 a name in the reserved namespace. */
  const UPGRADE_REQUIRED = 426;

  /* WHAT THE PLATFORM WILL CARRY, and it belongs to server/blobs.js — this is
     a copy, held to that one by tests/blob-limit.test.js.

     It is a copy because it cannot be an import: src/app/* are classic scripts
     that publish onto `window`, server/* is a module wrangler bundles for the
     edge, and neither can reach the other. So the rail is a test rather than a
     `from`, which is the same arrangement tests/blobs.test.js already keeps
     between the board's cap and the store's.

     The number is Cloudflare's Worker request-body cap on Free and Pro, not a
     ManDana rule about file sizes, and it is the conservative figure until the
     account's tier is confirmed. See server/blobs.js for the whole of why. */
  const MD_BLOB_MAX_BYTES = 100 * 1024 * 1024;

  /* The same sentence the server's 413 carries, because the person on the
     other end must not be able to tell which half refused — and because
     "file too large" names no wall and offers no way past it. */
  const TOO_LARGE = 'Cloudflare Workers carry at most 100 MB in one request body, '
    + 'so these bytes cannot reach the store. A smaller file will go.';

  function roomPath(room: string): string {
    return `/api/room/${encodeURIComponent(room)}`;
  }

  function socketUrl(room: string): string {
    const loc = g.location || { protocol: 'https:', host: '' };
    const scheme = loc.protocol === 'http:' ? 'ws:' : 'wss:';
    return `${scheme}//${loc.host}${roomPath(room)}`;
  }

  /* A BLOB HAS TWO NAMES ON A WRITE: the hash names the bytes; `ref` names the
     record keeping them. The second one used to disappear at this seam, which
     made every production hold `legacy` even though md-blobs.ts supplied the
     owner. Keeping the query in one helper makes put and release incapable of
     drifting back into two spellings of ownership. */
  function blobPath(hash: string, options?: any): string {
    const query: string[] = [];
    if (options && typeof options.type === 'string' && options.type) {
      query.push(`type=${encodeURIComponent(options.type)}`);
    }
    if (options && typeof options.ref === 'string' && options.ref) {
      query.push(`ref=${encodeURIComponent(options.ref)}`);
    }
    return `/api/blob/${hash}${query.length ? `?${query.join('&')}` : ''}`;
  }

  /** Is there a room here, and may this device be in it? See the header. */
  async function ask(room: string): Promise<{ ok: boolean; error?: string }> {
    let res: any = null;
    try {
      /* The cookie is not optional: without it an account room answers 401 to
         the device it belongs to. `no-store` because a refusal that got cached
         would outlive the reason for it. */
      res = await g.fetch(roomPath(room), { credentials: 'same-origin', cache: 'no-store' });
    } catch (err: any) {
      return { ok: false, error: (err && err.message) || String(err) };
    }
    const status = (res && res.status) || 0;
    if (status === UPGRADE_REQUIRED) return { ok: true };
    return { ok: false, error: `the room answered ${status}` };
  }

  /* THE ONE PLACE ALLOWED TO KNOW A STATUS CODE, turning it into a word from
     md-transport.ts's vocabulary. Everything above the seam reads the word.
     Unknown codes are `unreachable` rather than terminal: a store answering
     something this build has never seen is more likely to be a proxy in the
     way than a permanent refusal, and unreachable is the one reason worth
     trying again. */
  function reasonOf(status: number): string {
    if (status === 401 || status === 403) return 'unauthorised';
    if (status === 413) return 'too_large';
    return 'unreachable';
  }

  function makeCloudflare() {
    if (typeof g.WebSocket !== 'function') {
      throw new Error('this browser has no WebSocket, so a room cannot be joined');
    }
    if (typeof g.fetch !== 'function') {
      throw new Error('this browser has no fetch, so blobs cannot be read or written');
    }

    /* room -> socket + subscribers + two intentionally different queues.
       Reliable frames preserve order. Ephemeral frames preserve only the
       newest value for a coalesce key, which prevents yesterday's pointer
       positions from delaying today's final stroke after a reconnect. */
    const rooms = new Map<string, any>();

    function notify(state: any, value: any) {
      state.connection = value;
      for (const fn of [...state.observers.values()]) {
        if (typeof fn !== 'function') continue;
        try { fn(value); } catch { /* diagnostics must never break delivery */ }
      }
    }

    function queueBytes(state: any, bytes: Uint8Array, options: any) {
      const delivery = options && options.delivery === 'ephemeral' ? 'ephemeral' : 'reliable';
      if (delivery === 'ephemeral') {
        const key = String((options && options.coalesceKey) || 'ephemeral');
        if (!state.ephemeral.has(key) && state.ephemeral.size >= EPHEMERAL_KEYS_MAX) {
          state.ephemeral.delete(state.ephemeral.keys().next().value);
          state.dropped += 1;
        } else if (state.ephemeral.has(key)) {
          state.dropped += 1;
        }
        state.ephemeral.set(key, bytes);
        return { ok: true, queued: true, coalesced: true };
      }
      if (state.reliableBytes + bytes.byteLength > RELIABLE_QUEUE_MAX) {
        return { ok: false, error: 'the reliable room queue is full' };
      }
      state.reliable.push(bytes);
      state.reliableBytes += bytes.byteLength;
      return { ok: true, queued: true };
    }

    function flush(room: string) {
      const state = rooms.get(room);
      if (state) state.flushTimer = null;
      const socket = state && state.socket;
      if (!state || !socket || socket.readyState !== 1) return;
      while (state.reliable.length && Number(socket.bufferedAmount || 0) < CONGESTED_AT) {
        const bytes = state.reliable[0];
        try {
          socket.send(bytes);
          state.reliable.shift();
          state.reliableBytes -= bytes.byteLength;
        } catch {
          /* OPEN is only the socket's last observed state. A network can die
             between that read and send(); keep the reliable head in place and
             force the ordinary reconnect path to carry it. */
          try { socket.close(); } catch { /* onclose may already be running */ }
          break;
        }
      }
      if (!state.reliable.length && Number(socket.bufferedAmount || 0) < CONGESTED_AT) {
        const held = [...state.ephemeral.values()];
        state.ephemeral.clear();
        for (const bytes of held) {
          if (Number(socket.bufferedAmount || 0) >= CONGESTED_AT) {
            state.ephemeral.set(`flush:${state.flushKey++}`, bytes);
            continue;
          }
          try { socket.send(bytes); }
          catch {
            state.ephemeral.set(`flush:${state.flushKey++}`, bytes);
            try { socket.close(); } catch { /* onclose may already be running */ }
            break;
          }
        }
      }
      if ((state.reliable.length || state.ephemeral.size) && !state.flushTimer
        && state.socket && state.socket.readyState === 1) {
        state.flushTimer = g.setTimeout(() => flush(room), 16);
      }
    }

    function connect(room: string) {
      const state = rooms.get(room);
      if (!state || state.closed) return;

      let socket: any = null;
      try {
        socket = new g.WebSocket(socketUrl(room));
      } catch {
        retry(room);
        return;
      }
      socket.binaryType = 'arraybuffer';
      state.socket = socket;
      notify(state, { state: 'connecting' });

      socket.onopen = () => {
        state.retry = RETRY_FLOOR;
        notify(state, { state: 'open', droppedEphemeral: state.dropped });
        flush(room);
      };

      socket.onmessage = (event: any) => {
        /* Opaque. The one transformation here is ArrayBuffer -> Uint8Array,
           which is a view and not a decode. */
        const data = event && event.data;
        const bytes = data instanceof ArrayBuffer ? new Uint8Array(data)
          : ArrayBuffer.isView(data) ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
            : null;
        if (!bytes) return;           // a text frame is not ours; ignore rather than guess
        for (const fn of [...state.handlers]) {
          try { fn(bytes); } catch { /* one bad subscriber must not deafen the rest */ }
        }
      };

      socket.onclose = () => { state.socket = null; retry(room); };
      socket.onerror = () => { try { socket.close(); } catch { /* already gone */ } };
    }

    function retry(room: string) {
      const state = rooms.get(room);
      if (!state || state.closed || !state.handlers.size) return;
      const wait = state.retry;
      state.retry = Math.min(state.retry * 2, RETRY_CEILING);
      notify(state, { state: 'retrying', retryIn: wait, droppedEphemeral: state.dropped });
      state.timer = g.setTimeout(() => connect(room), wait);
    }

    function forget(room: string) {
      const state = rooms.get(room);
      if (!state) return;
      state.closed = true;
      if (state.timer) g.clearTimeout(state.timer);
      if (state.flushTimer) g.clearTimeout(state.flushTimer);
      notify(state, { state: 'closed', droppedEphemeral: state.dropped });
      if (state.socket) { try { state.socket.close(); } catch { /* already gone */ } }
      rooms.delete(room);
    }

    return {
      async subscribe(room: string, onMessage: (bytes: Uint8Array) => void, options?: any) {
        if (typeof room !== 'string' || !room || typeof onMessage !== 'function') {
          return { ok: false, error: 'a subscription needs a room and something to call' };
        }
        if (!rooms.has(room)) {
          const asked = await ask(room);
          /* Nothing is recorded on a refusal, which is what makes the doomed
             reconnect loop never start — and what makes the next subscribe ask
             again. The commonest reason a room says no is a session that
             expired or a network that went, and both come back. */
          if (!asked.ok) return asked;
          rooms.set(room, {
            socket: null, handlers: new Set(), observers: new Map(),
            reliable: [], reliableBytes: 0, ephemeral: new Map(), dropped: 0,
            retry: RETRY_FLOOR, timer: null, flushTimer: null, flushKey: 0,
            connection: { state: 'connecting' }, closed: false,
          });
          connect(room);
        }
        const state = rooms.get(room);
        state.handlers.add(onMessage);
        state.observers.set(onMessage, options && options.onState);
        if (options && typeof options.onState === 'function') {
          try { options.onState(state.connection); } catch { /* observer only */ }
        }
        return {
          ok: true,
          unsubscribe() {
            state.handlers.delete(onMessage);
            state.observers.delete(onMessage);
            if (!state.handlers.size) forget(room);
          },
        };
      },

      async publish(room: string, bytes: Uint8Array, options?: any) {
        const state = rooms.get(room);
        if (!state) return { ok: false, error: 'publish to a room nobody is in' };
        if (!(bytes instanceof Uint8Array)) return { ok: false, error: 'publish needs bytes' };
        const socket = state.socket;
        if (socket && socket.readyState === 1 && Number(socket.bufferedAmount || 0) < CONGESTED_AT
          && !state.reliable.length) {
          try { socket.send(bytes); return { ok: true }; }
          catch {
            /* A final scene frame must not disappear in the OPEN→dead race.
               Queue it under its requested delivery class, then let the same
               reconnect path used by onerror drain it on the next socket. */
            const queued = queueBytes(state, bytes, options);
            try { socket.close(); } catch { /* onclose may already be running */ }
            return queued;
          }
        }
        const queued = queueBytes(state, bytes, options);
        if (queued.ok && socket && socket.readyState === 1 && !state.flushTimer) {
          state.flushTimer = g.setTimeout(() => flush(room), 16);
        }
        return queued;
      },

      async blobPut(bytes: Uint8Array, options?: any) {
        /* BEFORE THE HASH AND BEFORE THE UPLOAD. A body over the cap is going
           to be dropped at the edge, and finding that out afterwards costs a
           minute of somebody's tethered connection and a SHA-256 over a
           hundred megabytes for an answer that was knowable at the first byte.

           `reason` rather than a status code, and the same word the server's
           413 maps to, so nothing above the seam can tell which side refused. */
        if (bytes && bytes.byteLength > MD_BLOB_MAX_BYTES) {
          return { ok: false, reason: 'too_large', error: TOO_LARGE };
        }
        let hash = '';
        try { hash = await g.mdTransportHash(bytes); }
        catch (err: any) { return { ok: false, error: (err && err.message) || String(err) }; }
        if (!hash) return { ok: false, error: 'this browser cannot hash, so a blob has no name' };
        try {
          const res = await g.fetch(blobPath(hash, options), {
            method: 'PUT',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/octet-stream' },
            body: bytes,
          });
          /* 409 is success. The blob is named by its content, so "it is already
             there" and "I just put it there" are the same outcome. */
          if (!res.ok && res.status !== 409) {
            /* The word as well as the sentence. A body that got past the
               pre-check can still be refused at the edge — a proxy with a
               tighter cap, a plan that is smaller than this build believes —
               and a caller must be able to tell that from a store that is
               merely unreachable, which is the one refusal worth retrying. */
            return {
              ok: false,
              reason: reasonOf(res.status),
              error: `the blob store answered ${res.status}`,
            };
          }
          return { ok: true, hash };
        } catch (err: any) {
          return { ok: false, error: (err && err.message) || String(err) };
        }
      },

      async blobGet(hash: string) {
        /* Checked before it reaches a URL, which is what keeps a path out of a
           bucket key. */
        if (!g.mdTransportIsHash(hash)) return { ok: false, error: 'that is not a blob name' };
        try {
          const res = await g.fetch(`/api/blob/${hash}`, { credentials: 'same-origin' });
          if (!res.ok) return { ok: false, error: `the blob store answered ${res.status}` };
          return { ok: true, bytes: new Uint8Array(await res.arrayBuffer()) };
        } catch (err: any) {
          return { ok: false, error: (err && err.message) || String(err) };
        }
      },

      /* A window of a blob, which R2 serves from the object itself rather than
         by reading it whole. Blossom's stores answer the same header the same
         way, which is why this is a contract verb and not a Cloudflare trick.

         THE WIRE IS INCLUSIVE AND THIS SEAM IS NOT. Every range above the line
         is half-open [start, end), because that is how a JavaScript slice
         reads and mixing the two conventions is how a player ends up one byte
         short of a frame. The single `- 1` here is the whole of the
         translation, and it lives at the edge where the wire begins. */
      async blobGetRange(hash: string, start: number, end: number) {
        if (!g.mdTransportIsHash(hash)) return { ok: false, error: 'that is not a blob name' };
        if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end <= start) {
          return { ok: false, error: 'that is not a window' };
        }
        try {
          const res = await g.fetch(`/api/blob/${hash}`, {
            credentials: 'same-origin',
            headers: { Range: `bytes=${start}-${end - 1}` },
          });
          if (!res.ok) return { ok: false, error: `the blob store answered ${res.status}` };
          const bytes = new Uint8Array(await res.arrayBuffer());
          /* A 200 means somebody along the way did not honour the header — a
             proxy, a cache, an older Worker. The bytes are still the right
             bytes, so the window is taken here instead of the read being
             failed over a header nobody promised. */
          if (res.status !== 206) return { ok: true, bytes: bytes.slice(start, end) };
          return { ok: true, bytes };
        } catch (err: any) {
          return { ok: false, error: (err && err.message) || String(err) };
        }
      },

      /* Blossom's DELETE /<sha256>. What goes is THIS account's hold; whether
         the object goes with it is the server's decision, because an object two
         accounts uploaded is not one account's to remove. */
      async blobRelease(hash: string, ref?: string) {
        /* Checked before it reaches a URL, exactly as blobGet does — and it
           matters more here, because this one is a DELETE. */
        if (!g.mdTransportIsHash(hash)) return { ok: false, error: 'that is not a blob name' };
        try {
          const res = await g.fetch(blobPath(hash, { ref }), {
            method: 'DELETE',
            credentials: 'same-origin',
          });
          /* 404 is success, for the same reason 409 is success on a put: the
             end state asked for is "this is not stored", and a store that
             never had it is already in that state. */
          if (res.status === 404) return { ok: true, collected: true };
          if (!res.ok) return { ok: false, error: `the blob store answered ${res.status}` };

          /* The hold is released by the request arriving, not by the body
             parsing. A proxy that rewrote the answer must not turn a release
             that worked into one the caller retries for ever — so an
             unreadable body costs the `collected` detail and nothing else. */
          let collected = false;
          try {
            const body = await res.json();
            collected = !!(body && body.collected);
          } catch (err: any) { collected = false; }
          return { ok: true, collected };
        } catch (err: any) {
          return { ok: false, error: (err && err.message) || String(err) };
        }
      },

      /* Blossom's GET /list/<pubkey>, which here is "list what this session's
         account holds" — the account is the cookie's to know, not a parameter
         a client gets to choose. Rows are NORMALISED before they leave: a
         Blossom server answers `sha256`, this one answers `hash`, and above
         the seam there is one shape.

         A 200 IS NOT AN ANSWER. A host without this route serves index.html
         with status 200, and JSON.parse on a page of HTML throws — but a host
         that answered `{}` would parse fine and read as "no blobs at all",
         which is the one lie blobUsage exists to prevent. So the shape is
         checked, and anything that is not the shape is `unreachable`. */
      async blobList() {
        let res: any = null;
        try {
          res = await g.fetch('/api/blob/list', { credentials: 'same-origin' });
        } catch (err: any) {
          return { ok: false, reason: 'unreachable' };
        }
        if (!res.ok) return { ok: false, reason: reasonOf(res.status) };
        let body: any = null;
        try { body = await res.json(); } catch (err: any) { body = null; }
        if (!body || !Array.isArray(body.blobs)) return { ok: false, reason: 'unreachable' };
        const blobs = [];
        for (const row of body.blobs) {
          if (!row || typeof row !== 'object') continue;
          const hash = typeof row.hash === 'string' ? row.hash : row.sha256;
          if (!g.mdTransportIsHash(hash)) continue;
          blobs.push({
            hash,
            size: Number(row.size) || 0,
            type: typeof row.type === 'string' ? row.type : '',
            uploaded: Number(row.uploaded) || 0,
          });
        }
        return { ok: true, blobs };
      },

      /* THE TOTAL, ASKED FOR RATHER THAN ADDED UP. The seam supplies this verb
         to any transport that only lists — see usageFromList — and that
         derivation is correct here too. It is also ten thousand rows of work
         for a number the server has already computed, and it can never report
         the allowance, which is not a fact about the blobs but about the
         account's plan. A store that knows should say.

         `allowance: null` is kept for an answer that states none, exactly as
         the derived sum reports it: a number invented on this side would be a
         client constant impersonating a server's policy, and it would be the
         first thing to go stale.

         A 200 IS NOT AN ANSWER, and it matters more here than on the list. A
         host without this route serves index.html with status 200; JSON.parse
         on a page of HTML throws and is caught, but a proxy answering `{}`
         parses cleanly and would read as `bytes: 0` — telling somebody their
         account is empty while it is full. So the shape is checked, and
         anything that is not the shape is `unreachable`. */
      async blobUsage() {
        let res: any = null;
        try {
          res = await g.fetch('/api/blob/usage', { credentials: 'same-origin' });
        } catch (err: any) {
          return { ok: false, reason: 'unreachable' };
        }
        if (!res.ok) return { ok: false, reason: reasonOf(res.status) };
        let body: any = null;
        try { body = await res.json(); } catch (err: any) { body = null; }
        const number = (value: any) => typeof value === 'number' && Number.isFinite(value);
        if (!body || !number(body.bytes) || !number(body.count)) {
          return { ok: false, reason: 'unreachable' };
        }
        return {
          ok: true,
          bytes: body.bytes,
          count: body.count,
          allowance: number(body.allowance) ? body.allowance : null,
        };
      },

      close() {
        for (const room of [...rooms.keys()]) forget(room);
      },
    };
  }

  if (typeof g.mdTransportRegister === 'function') g.mdTransportRegister('cf', makeCloudflare);
})(typeof window !== 'undefined' ? window : globalThis);
